import consts from "consts/index"
import FormatterHelper from "helpers/formatter"
import routeHelpers from "helpers/route"
import UrlHelper from "helpers/url"
import { useDispatch, useSelector } from "react-redux"

import { push } from "connected-react-router"

import i18n from "consts/i18n"
import selectAuthentication from "selectors/authentication"
import { selectQuery } from "selectors/location"
import routes from "consts/routes"
import { selectChannels } from "selectors/references"
import { matchPath } from "react-router-dom"
import { selectIsMobile } from "selectors/navigator"

export const useOnCTAClick = ({ program = { pending: true } } = {}) => {
  const isMobile = useSelector(selectIsMobile)
  const pathname = useSelector(state => state.router.location.pathname)
  const isChannelPage = matchPath(pathname, {
    path: routes.channel,
  })
  const isReplayPage = matchPath(pathname, {
    path: routes.channelReplay,
  })
  const isDeeplinkPage = matchPath(pathname, {
    path: routes.deeplink,
  })
  const dispatch = useDispatch()
  const query = useSelector(selectQuery)
  const authentication = useSelector(selectAuthentication)
  const channelOffer = useSelector(state => selectChannels(state)?.direct?.offer)
  const downloadCampaign = isDeeplinkPage ? "deeplink" : "content"
  const downloadAdGroup = isDeeplinkPage ? routeHelpers.getAdGroupFromQuery(query) : routeHelpers.getAdGroupFromPathname(pathname)

  const trackUserAction = clickTarget => {
    const target = clickTarget || "main_cta"

    window.dataLayer &&
      window.dataLayer.push({
        event: "gtm.custom_event",
        event_name: "watch_now_cta_click",
        event_category: "watch_now",
        event_action: "cta_clicked",
        event_target: `${target}`,
      })

    //@dev save action to track further path within the funnel
    sessionStorage.setItem("watchNowCtaTarget", JSON.stringify(target))
  }

  const openInBrowser = () => {
    const programMeta = program?.data?.metadata
    const baseUrl = program?.isLive()
      ? `/channels/${programMeta?.channel_id}`
      : `/channel/${programMeta?.channel_id}/program/${programMeta?.program_id}`

    const query = {
      ...(program.isLive() && { live_program_id: programMeta.program_id }),
      origin: "programtv.seo",
      utm_medium: "seo",
      utm_campaign: `page_seo_${programMeta.channel_name}`,
    }

    const parsedQuery = routeHelpers.objectToQuery(query)

    const url = `${baseUrl}?${parsedQuery}`

    UrlHelper.openInBrowser(url, true)
  }

  const redirectToProgramOffer = ({ programOffer, isMobile = false } = {}) => {
    // we want to redirect a user to the app immediately after payment
    // thus we have to store redirection url && subscription info in a session storage for further redirection
    const programMeta = program?.data?.metadata

    const getUrl = () => {
      if (isMobile) {
        return routeHelpers.getDownloadURL(downloadCampaign, downloadAdGroup)
      }
      // @dev here program.pending === no program was chosen, it is redirection from programme-tv page or movies page
      if (pathname.includes("movies")) {
        return "/category/1"
      }

      if (!program.pending && program?.isLive()) {
        return `/channels/${programMeta?.channel_id}`
      }

      if (!program.pending) {
        return `/channel/${programMeta?.channel_id}/program/${programMeta?.program_id}`
      }

      return "/home"
    }

    const url = getUrl()

    const offer = programOffer || channelOffer

    const storageData = {
      url,
      programOffer: offer ? { ...offer, isMobile } : { freeTier: true, isMobile },
    }

    sessionStorage.setItem(consts.sessionStorageKey.programOfferToSubscribe, JSON.stringify(storageData))

    if (storageData.programOffer.freeTier) {
      dispatch(push(FormatterHelper.basic(`${consts.routes.offers}?signup=true`)))
      return
    }

    dispatch(push(FormatterHelper.basic(`${consts.routes.subscribePage}&signup=true`, { equivalenceCode: offer?.product_equivalence_code })))
  }

  const onBrowserCTAClick = () => {
    const programOffer = program?.data?.offer
    const programSlug = program?.data?.slug

    const baseUrl = pathname.includes("movies") ? "/category/1" : "/home"

    trackUserAction(programSlug)

    if (!program?.pending && program?.getBroadcastDate() === i18n.not_broadcasted) {
      UrlHelper.openInBrowser("", true)
      return
    }

    if (program.pending && !isChannelPage && !isReplayPage) {
      if (!authentication?.isAuthenticated) {
        sessionStorage.setItem(consts.sessionStorageKey.programOfferToSubscribe, JSON.stringify({ url: baseUrl, programOffer: { freeTier: true } }))
        dispatch(push(FormatterHelper.basic(`${consts.routes.offers}?signup=true`)))
        return
      }

      UrlHelper.openInBrowser(baseUrl, true)
      return
    }

    // if the program goes under a paid subscription
    // redirect to a /subscribe page (there is an authorisation wall already, no need in any additional steps)
    // OR
    // there is an offer === user is not subscribed to a product
    if (programOffer?.product_equivalence_code) {
      redirectToProgramOffer({ programOffer })
      return
    }

    // product is from a free tier
    if (!authentication?.isAuthenticated) {
      redirectToProgramOffer()
      return
    }

    // no offer === program is free || user already subscribed
    openInBrowser()
  }

  const onMobileClick = () => {
    const programOffer = program?.data?.offer
    const programSlug = program?.data?.slug

    trackUserAction(programSlug)

    if (!program?.pending && program?.getBroadcastDate() === i18n.not_broadcasted) {
      window.location = routeHelpers.getDownloadURL(downloadCampaign, downloadAdGroup)
      return
    }

    // TODO on link page, it should be set to true
    // if (false) {
    //   UrlHelper.openInBrowser("/home", true)
    //   return
    // }

    if (program.pending && !isChannelPage && !isReplayPage) {
      sessionStorage.setItem(
        consts.sessionStorageKey.programOfferToSubscribe,
        JSON.stringify({
          url: routeHelpers.getDownloadURL(downloadCampaign, downloadAdGroup),
          programOffer: { freeTier: true, isMobile: true },
        })
      )
      dispatch(push(FormatterHelper.basic(`${consts.routes.offers}?signup=true`)))
      return
    }

    // if the program goes under a paid subscription
    // redirect to a /subscribe page (there is an authorisation wall already, no need in any additional steps)
    // OR
    // there is an offer === user is not subscribed to a product
    if (programOffer?.product_equivalence_code) {
      redirectToProgramOffer({ programOffer, isMobile: true })
      return
    }

    if (!authentication?.isAuthenticated) {
      // product is from a free tier
      redirectToProgramOffer({ isMobile: true })
      return
    }

    // no offer === program is free || user already subscribed
    window.location = routeHelpers.getDownloadURL(downloadCampaign, downloadAdGroup)
  }

  return isMobile ? onMobileClick : onBrowserCTAClick
}
